/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ArrowForward } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { nextOrderItemStatus } from '../../api/order/nextOrderItemStatus';
import { Order, OrderItem } from '../../apiTypes';
import Image from '../../components/Image';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import EditExcises from './EditExcises';

type Props = {
  item: OrderItem;
  order: DbDocument<Order>;
  disabled?: boolean;
  currentTime: number;
  showPBar?: boolean;
  showInfo?: boolean;
};

const KitchenItem = ({ item, disabled, order, currentTime, showPBar, showInfo }: Props) => {
  const { dishesMap, worker, orders } = useCafe();
  const { cafeSlug, timeForWaiting, cafe } = useAdmin();
  const { lang } = useLocale();
  const [editExcises, setEditExcises] = useState(false);

  const { time = 0, taxInfo } = dishesMap[item.dishId] || {};
  const hasExcise = taxInfo?.hasExcise;

  const showProgressBar =
    showPBar &&
    ((item.status === 'ordered' && item.queue === '1') || (item.status === 'processing' && item.startProcessingTime));

  const minAgo = useMemo(() => {
    const min = Math.round((currentTime - item.addTime) / 60000);
    return min > 60 ? '> 60' : min;
  }, [currentTime, item.addTime]);

  const percentToReady = useMemo(() => {
    if (showProgressBar) {
      const diff = currentTime
        ? (currentTime - (item.status === 'ordered' ? item.addTime : item.startProcessingTime || 0)) / 60000
        : 0;
      const limit = item.status === 'ordered' ? timeForWaiting : time;
      const percentCalculation = Math.round(((Number(limit) - diff) * 100) / Number(limit));
      return percentCalculation > 0 ? percentCalculation : 0;
    }
    return 0;
  }, [currentTime, item.addTime, item.startProcessingTime, item.status, showProgressBar, time, timeForWaiting]);

  const changeStatus = useCallback(async () => {
    const dish = dishesMap[item.dishId];
    const fullOrder = orders.find((o) => o.id === order.id);

    if (
      disabled ||
      item.status === 'complete' ||
      !worker ||
      !dish ||
      !fullOrder ||
      !cafe ||
      !worker.credentials.change_order_statuses
    ) {
      return;
    }

    await nextOrderItemStatus({ order: fullOrder, worker, item, cafeSlug, dish, cafe });
  }, [cafeSlug, disabled, dishesMap, item, order.id, orders, worker, cafe]);

  const handleClick = useCallback(async () => {
    if (item.status === 'ordered' && hasExcise) {
      setEditExcises(true);
    } else {
      changeStatus();
    }
  }, [changeStatus, hasExcise, item.status]);

  const handleClose = useCallback(() => {
    setEditExcises(false);
    changeStatus();
  }, [changeStatus]);

  return (
    <Box
      key={item.id}
      sx={{
        position: 'relative',
        display: 'flex',
        p: 0.5,
        mb: 0.5,
        width: 'calc(100% - 8px)',
        borderRadius: 1,
        borderStyle: showProgressBar ? 'solid' : 'none',
        borderWidth: 2,
        borderColor: (t) => (percentToReady === 0 ? t.palette.error.light : `${t.palette.secondary.light}20`),
        opacity: disabled ? 0.3 : 1,
        cursor: 'pointer',
        ':hover': {
          bgcolor: (t) => (disabled ? undefined : `${t.palette.secondary.main}20`),
        },
      }}
      onClick={handleClick}
    >
      <Box sx={{ mr: 1, height: 40 }}>
        <Image src={dishesMap[item.dishId]?.imageUrl} />
      </Box>
      <Box sx={{ overflow: 'hidden', flexGrow: 1, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Typography sx={{ lineHeight: 1.1, mb: 0.5 }}>{dishesMap[item.dishId]?.name}</Typography>

          {item.variation && (
            <Typography
              variant="body2"
              sx={{ lineHeight: 1 }}
              color={(t) => (item.status === 'complete' ? t.palette.text.disabled : t.palette.text.secondary)}
            >
              {item.variation.name}
            </Typography>
          )}
          {item.modifications?.map((mod) => (
            <Typography key={mod.name} variant="body2" sx={{ lineHeight: 1 }} color={(t) => t.palette.text.secondary}>
              {mod.name}
            </Typography>
          ))}
          {item.comment && (
            <Typography variant="body2" sx={{ lineHeight: 1 }} color={(t) => t.palette.text.secondary}>
              {item.comment}
            </Typography>
          )}
        </div>
        {showInfo && (
          <Typography
            variant="body2"
            textAlign="center"
            alignSelf="center"
            color={(t) => (percentToReady === 0 ? t.palette.error.main : t.palette.text.disabled)}
            sx={{ width: 52, lineHeight: 1, fontSize: 12 }}
          >{`${minAgo} ${t.min_ago[lang]}`}</Typography>
        )}
      </Box>

      <Box width={40}>
        {!disabled && (
          <IconButton>
            <ArrowForward />
          </IconButton>
        )}
      </Box>

      {showProgressBar && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: `${percentToReady}%`,
            borderTopLeftRadius: 6,
            borderBottomLeftRadius: 6,
            bgcolor: (t) => `${percentToReady === 0 ? t.palette.error.light : t.palette.secondary.light}20`,
          }}
        />
      )}

      {editExcises && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <EditExcises open orderId={order.id} item={item} onClose={handleClose} />
        </div>
      )}
    </Box>
  );
};

export default KitchenItem;
